import { Link, Route, Routes, useParams } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import { useEffect, useMemo, useState } from 'react'

type PostMeta = {
  slug: string
  title: string
  date: string
  excerpt: string
}

type IndexPayload = {
  posts: PostMeta[]
}

function Shell(props: { children: React.ReactNode }) {
  return (
    <div className="page">
      <header className="header">
        <div className="headerInner">
          <Link className="brand" to="/">
            <span className="brandMark">CID</span>
            <span className="brandName">Corporate IT Detox</span>
          </Link>
          <div className="tagline">Stories about corporate IT projects that seem crazy.</div>
        </div>
      </header>
      <main className="main">{props.children}</main>
      <footer className="footer">
        <div className="footerInner">
          <span>This story is a fictionalized scenario inspired by the absurdities of corporate IT. Any resemblance to real projects, tools, or colleagues is purely coincidental. Some content created with a little help from AI (because even imagination needs backup sometimes).</span>
        </div>
      </footer>
    </div>
  )
}

function Home() {
  const [data, setData] = useState<IndexPayload | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch('/content/index.json', { cache: 'no-store' })
        if (!res.ok) throw new Error(`Failed to load content index (${res.status})`)
        const json = (await res.json()) as IndexPayload
        if (!cancelled) setData(json)
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Unknown error')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const posts = useMemo(() => data?.posts ?? [], [data])

  return (
    <Shell>
      <section className="hero">
        <h1 className="heroTitle">Corporate IT Detox</h1>
        <p className="heroBody">
          A small blog for when a two-line change request becomes a multi-quarter transformation program.
        </p>
      </section>

      <section className="list">
        <div className="listHeader">
          <h2>Latest</h2>
        </div>
        {error ? <div className="error">{error}</div> : null}
        {!data && !error ? <div className="muted">Loading…</div> : null}
        {posts.map((p) => (
          <article key={p.slug} className="card">
            <div className="cardMeta">
              <time dateTime={p.date}>{p.date}</time>
            </div>
            <h3 className="cardTitle">
              <Link to={`/post/${p.slug}`}>{p.title}</Link>
            </h3>
            <p className="cardExcerpt">{p.excerpt}</p>
            <div className="cardActions">
              <Link className="readMore" to={`/post/${p.slug}`}>
                Read
              </Link>
            </div>
          </article>
        ))}
      </section>
    </Shell>
  )
}

function Post() {
  const { slug } = useParams()
  const [md, setMd] = useState<string>('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        if (!slug) throw new Error('Missing slug')
        const res = await fetch(`/content/${slug}.md`, { cache: 'no-store' })
        if (!res.ok) throw new Error(`Post not found (${res.status})`)
        const text = await res.text()
        if (!cancelled) setMd(text)
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Unknown error')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [slug])

  return (
    <Shell>
      <div className="crumbs">
        <Link to="/">Home</Link>
        <span className="crumbSep">/</span>
        <span className="crumbHere">Post</span>
      </div>

      {error ? <div className="error">{error}</div> : null}
      {!md && !error ? <div className="muted">Loading…</div> : null}

      {md ? (
        <article className="post">
          <ReactMarkdown>{md}</ReactMarkdown>
        </article>
      ) : null}
    </Shell>
  )
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/post/:slug" element={<Post />} />
      <Route
        path="*"
        element={
          <Shell>
            <div className="error">Not found.</div>
            <div className="muted">
              <Link to="/">Go home</Link>
            </div>
          </Shell>
        }
      />
    </Routes>
  )
}
